import { EmptyState, Text } from '@box/blueprint-web';
import Files from '@box/blueprint-web-assets/illustrations/Medium/Files';
import Button from 'box-ui-elements/es/components/button/Button';
import LinkPrimaryButton from 'box-ui-elements/es/components/link/LinkPrimaryButton';
import React from 'react';
import type { IntlShape } from 'react-intl';
import { injectIntl } from 'react-intl';
import { useShouldRenderBlueprint } from '../../../common/useShouldRenderBlueprint';
import isAnonymousUser from '../../../common/utils/isAnonymousUser';
import VariantComponent from '../../../common/variant-component';
import SuccessFormIcon from '../../../assets/icons/success-form';
import type { User } from '../../../types/User';
import css from './completed-form-page.module.scss';
import messages from './messages';

type CompletedFormStateProps = {
  currentUser: User;
  formType: string;
  intl: IntlShape;
  isAuthenticationRequired: boolean;
  numFiles: number;
};

const isIFrame = () => {
  try {
    return window.self !== window.top;
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  } catch (e: any) {
    return true;
  }
};

const OldCompletedFormState = ({
  currentUser,
  intl,
  isAuthenticationRequired,
  numFiles,
  formType,
}: CompletedFormStateProps) => {
  const message =
    formType === 'form'
      ? intl.formatMessage(messages.completedFormMessage)
      : intl.formatMessage(messages.completedFileRequestMessage, { numFiles });
  const { accountHost } = currentUser;
  const onGoToMyAccount = () => {
    window.location.href = `${accountHost}`;
  };

  return (
    <>
      <div className={css.message}>{message}</div>
      <SuccessFormIcon />
      {isAuthenticationRequired && !isAnonymousUser(currentUser) && (
        <div className={css.goToMyAccount}>
          {isIFrame() ? (
            <LinkPrimaryButton
              data-testid="button-to-tab-to-box"
              href={accountHost}
              target="_blank"
            >
              {intl.formatMessage(messages.goToMyAccount)}
            </LinkPrimaryButton>
          ) : (
            <Button
              className="btn btn-primary"
              data-testid="button-to-box"
              onClick={onGoToMyAccount}
            >
              {intl.formatMessage(messages.goToMyAccount)}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

const NewCompletedFormState = ({
  currentUser,
  intl,
  isAuthenticationRequired,
  numFiles,
  formType,
}: CompletedFormStateProps) => {
  const message =
    formType === 'form'
      ? intl.formatMessage(messages.completedFormMessage)
      : intl.formatMessage(messages.completedFileRequestMessage, { numFiles });

  const { accountHost } = currentUser;
  const onGoToMyAccount = () => {
    if (isIFrame()) {
      window.open(accountHost, '_blank', 'noreferrer');
    } else {
      window.location.href = `${accountHost}`;
    }
  };

  return (
    <EmptyState
      body={
        <Text as="p" color="textOnLightDefault" variant="titleLarge">
          {message}
        </Text>
      }
      illustration={Files}
    >
      {isAuthenticationRequired && !isAnonymousUser(currentUser) && (
        <EmptyState.PrimaryAction onClick={onGoToMyAccount}>
          {intl.formatMessage(messages.goToMyAccount)}
        </EmptyState.PrimaryAction>
      )}
    </EmptyState>
  );
};

const CompletedFormState = (props: CompletedFormStateProps) => {
  // Should be addressed in FORMS-21235
  const shouldRenderBlueprint = useShouldRenderBlueprint();
  return (
    <VariantComponent
      {...props}
      NewComponent={NewCompletedFormState}
      OldComponent={OldCompletedFormState}
      shouldRenderNewComponent={shouldRenderBlueprint}
    />
  );
};

export default injectIntl(CompletedFormState);
